/**
 * ShareModal Component
 *
 * Modal dialog for creating and managing share links for a reflection.
 * Two tabs: "Create Link" to generate new share URLs, and "Active Links"
 * to view/revoke existing shares and see the access log.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  X,
  Copy,
  Check,
  Link2,
  FileText,
  Headphones,
  Lightbulb,
  MessageSquare,
  Trash2,
  Users,
  Loader2,
  Send,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useShares, useCreateShare, useRevokeShare } from "@/hooks/useShares";
import { getSentInvitations, createInvitation } from "@/services/invitations";
import type { ReflectionResponse } from "@/types/reflection";
import type { ShareResponse } from "@/types/share";
import type { InvitationResponse } from "@/types/invitation";

interface ShareModalProps {
  reflection: ReflectionResponse;
  onClose: () => void;
  initialTab?: "create" | "active";
}

const EXPIRY_OPTIONS = [
  { label: "1 hour", minutes: 60 },
  { label: "24 hours", minutes: 1440 },
  { label: "7 days", minutes: 10080 },
  { label: "30 days", minutes: 43200 },
] as const;

export function ShareModal({
  reflection,
  onClose,
  initialTab = "create",
}: ShareModalProps) {
  const [tab, setTab] = useState<"create" | "active" | "shared">(initialTab);
  const [includeTranscript, setIncludeTranscript] = useState(true);
  const [includeAudio, setIncludeAudio] = useState(false);
  const [includeFeedback, setIncludeFeedback] = useState(true);
  const [includeChat, setIncludeChat] = useState(false);
  const [expiryMinutes, setExpiryMinutes] = useState(1440);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const queryClient = useQueryClient();

  const { data: shares, isLoading: sharesLoading } = useShares(reflection.id);
  const createShare = useCreateShare();
  const revokeShare = useRevokeShare(reflection.id);

  const { data: sentInvitations, isLoading: sentLoading } = useQuery({
    queryKey: ["invitations", "sent", reflection.id],
    queryFn: () => getSentInvitations(reflection.id),
    enabled: tab === "shared",
  });

  const sendInviteMutation = useMutation({
    mutationFn: (email: string) => createInvitation(reflection.id, email),
    onSuccess: () => {
      setInviteEmail("");
      setInviteError(null);
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 3000);
      queryClient.invalidateQueries({
        queryKey: ["invitations", "sent", reflection.id],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err instanceof Error ? err.message : "Failed to send invitation");
      setInviteError(detail);
    },
  });

  const activeShares = (shares || []).filter(
    (s) => s.is_active && new Date(s.expires_at) > new Date()
  );

  const canCreate =
    includeTranscript || includeFeedback || includeChat || includeAudio;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreateError(null);
    // Capture current state to avoid stale closure
    const payload = {
      reflection_id: reflection.id,
      expires_in_minutes: expiryMinutes,
      include_transcript: includeTranscript,
      include_audio: includeAudio,
      include_feedback: includeFeedback,
      include_chat: includeChat,
    };
    try {
      const result = await createShare.mutateAsync(payload);
      // Build URL from current origin so it works on any environment
      setCreatedUrl(`${window.location.origin}/share/${result.token}`);
    } catch (err: unknown) {
      console.error("Failed to create share:", err);
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err instanceof Error ? err.message : null) ||
        "Failed to generate link. Please try again.";
      setCreateError(msg);
    }
  };

  const handleCopyUrl = async () => {
    if (!createdUrl) return;
    try {
      await navigator.clipboard.writeText(createdUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleRevoke = (shareId: string) => {
    revokeShare.mutate(shareId);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Share Reflection
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {reflection.title || "Untitled Reflection"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 hover:bg-muted rounded-md"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border flex-shrink-0">
          <button
            onClick={() => setTab("create")}
            className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
              tab === "create"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Create Link
          </button>
          <button
            onClick={() => setTab("active")}
            className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
              tab === "active"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Active Links{" "}
            {activeShares.length > 0 && (
              <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                {activeShares.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("shared")}
            className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
              tab === "shared"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Shared With
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {tab === "create" && (
            <div className="space-y-5">
              {createdUrl ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Link created!</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={createdUrl}
                      className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-muted/50 text-foreground"
                    />
                    <Button size="sm" variant="outline" onClick={handleCopyUrl}>
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCreatedUrl(null);
                      setIncludeTranscript(true);
                      setIncludeAudio(false);
                      setIncludeFeedback(true);
                      setIncludeChat(false);
                    }}
                  >
                    Create another
                  </Button>
                </div>
              ) : (
                <>
                  {/* What to share — Switch toggles */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      What to share
                    </label>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-foreground">
                              Transcript
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Full conversation text with speaker labels
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={includeTranscript}
                          onCheckedChange={(v) => {
                            setIncludeTranscript(v);
                            if (!v) setIncludeAudio(false);
                          }}
                        />
                      </div>
                      <div
                        className={`flex items-center justify-between p-3 rounded-lg border border-border ${
                          !includeTranscript ? "opacity-50" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Headphones className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-foreground">Audio</p>
                            <p className="text-xs text-muted-foreground">
                              Original audio recording (time-limited URL)
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={includeAudio}
                          onCheckedChange={setIncludeAudio}
                          disabled={!includeTranscript}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                        <div className="flex items-center gap-3">
                          <Lightbulb className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-foreground">
                              Therapist Insights
                            </p>
                            <p className="text-xs text-muted-foreground">
                              AI feedback, themes, and session summary
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={includeFeedback}
                          onCheckedChange={setIncludeFeedback}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                        <div className="flex items-center gap-3">
                          <MessageSquare className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-foreground">
                              Chat History
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Follow-up conversation with the AI therapist
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={includeChat}
                          onCheckedChange={setIncludeChat}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Expiry — Dropdown */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Link expires after
                    </label>
                    <Select
                      value={String(expiryMinutes)}
                      onValueChange={(v) => setExpiryMinutes(Number(v))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPIRY_OPTIONS.map((opt) => (
                          <SelectItem
                            key={opt.minutes}
                            value={String(opt.minutes)}
                          >
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Helper text when nothing selected */}
                  {!canCreate && (
                    <p className="text-xs text-muted-foreground text-center">
                      Select at least one item above to generate a link
                    </p>
                  )}

                  {/* Error state */}
                  {createError && (
                    <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 rounded-md px-3 py-2">
                      {createError}
                    </p>
                  )}

                  {/* Generate button */}
                  <Button
                    className="w-full"
                    disabled={!canCreate || createShare.isPending}
                    onClick={handleCreate}
                  >
                    {createShare.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    Generate Link
                  </Button>
                </>
              )}
            </div>
          )}

          {tab === "active" && (
            <div className="space-y-3">
              {sharesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : activeShares.length === 0 ? (
                <div className="text-center py-8 space-y-2">
                  <Link2 className="w-8 h-8 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    No active share links
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTab("create")}
                  >
                    Create one
                  </Button>
                </div>
              ) : (
                activeShares.map((share: ShareResponse) => (
                  <ShareRow
                    key={share.id}
                    share={share}
                    onRevoke={handleRevoke}
                  />
                ))
              )}
            </div>
          )}

          {tab === "shared" && (
            <div className="space-y-5">
              {/* Invite form */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Share with an AttuneWell user
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={inviteEmail}
                    onChange={(e) => {
                      setInviteEmail(e.target.value);
                      setInviteError(null);
                      setInviteSuccess(false);
                    }}
                    className="flex-1"
                  />
                  <Button
                    size="sm"
                    disabled={!inviteEmail || sendInviteMutation.isPending}
                    onClick={() => sendInviteMutation.mutate(inviteEmail)}
                  >
                    {sendInviteMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {inviteError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {inviteError}
                  </p>
                )}
                {inviteSuccess && (
                  <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                    <Check className="w-4 h-4" /> Invitation sent!
                  </p>
                )}
              </div>

              {/* Sent invitations list */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Sent invitations
                </label>
                {sentLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                ) : !sentInvitations || sentInvitations.length === 0 ? (
                  <div className="text-center py-6">
                    <Mail className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No invitations sent yet
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sentInvitations.map((inv: InvitationResponse) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between p-3 border border-border rounded-lg"
                      >
                        <span className="text-sm text-foreground truncate">
                          {inv.recipient_email}
                        </span>
                        <Badge
                          variant="secondary"
                          className={
                            inv.status === "accepted"
                              ? "bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800"
                              : inv.status === "declined"
                                ? "bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800"
                                : inv.status === "expired"
                                  ? "bg-muted text-muted-foreground border-border"
                                  : "bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800"
                          }
                        >
                          {inv.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end flex-shrink-0">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Row component for an active share link
 */
function ShareRow({
  share,
  onRevoke,
}: {
  share: ShareResponse;
  onRevoke: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/share/${share.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const badges = [];
  if (share.include_transcript) badges.push("Transcript");
  if (share.include_audio) badges.push("Audio");
  if (share.include_feedback) badges.push("Insights");
  if (share.include_chat) badges.push("Chat");

  const totalAccess = share.access_log.reduce(
    (sum, a) => sum + a.access_count,
    0
  );

  return (
    <div className="border border-border rounded-lg">
      <div
        className="p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1 mb-1">
            {badges.map((b) => (
              <span
                key={b}
                className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded"
              >
                {b}
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Expires{" "}
            {new Date(share.expires_at).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {totalAccess > 0 && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="w-3 h-3" /> {totalAccess}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="text-muted-foreground hover:text-foreground"
            title="Copy link"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRevoke(share.id);
            }}
            className="text-destructive hover:text-destructive"
            title="Revoke link"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {expanded && share.access_log.length > 0 && (
        <div className="border-t border-border px-3 py-2 space-y-1">
          {share.access_log.map((entry, i) => (
            <div
              key={i}
              className="flex items-center justify-between text-xs text-muted-foreground"
            >
              <span>{entry.viewer_email}</span>
              <span>
                {entry.verified_at
                  ? new Date(entry.verified_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })
                  : "Pending"}
                {entry.access_count > 1 && ` (${entry.access_count}x)`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
